/**
 * Post-login route **chunk** warmup planning (module key order).
 *
 * Reads `routing.postLoginChunkPreloadOrder` and `routing.postLoginChunkPreloadAudience`
 * from the module catalog (`APP_MODULE_CATALOG` ← `appModuleDefinitions.js`).
 *
 * Returns module **keys** sorted by priority; `routeChunkLoaders.js` maps keys to
 * `routeId` and runs `preloadRouteChunk`. i18n warmup is a separate system (`i18n.js`).
 *
 * @module modules/appModulePreload
 * @see docs/route-chunk-preload.md
 */

import { canAccessModule } from "./appModuleAccess";
import { getModuleCatalogEntries } from "./appModuleCatalog";
import { USER_ROLES } from "./appModuleKeys";

/** Case-insensitive role string for audience matching. */
function normalizeRole(role) {
  return typeof role === "string" ? role.trim().toLowerCase() : "";
}

/**
 * Evaluators only receive modules tagged `postLoginChunkPreloadAudience: "evaluator"`.
 * Everyone else uses `"standard"` (the default when audience is omitted).
 */
function resolvePostLoginAudience(userData) {
  return normalizeRole(userData?.role) === USER_ROLES.EVALUATOR
    ? "evaluator"
    : "standard";
}

/**
 * Returns module keys for authenticated post-login chunk warmup, in priority order.
 *
 * Filters applied (in order):
 * 1. `canAccessModule` — respects assignment, roles, devOnly, custom policy
 * 2. Has finite `routing.postLoginChunkPreloadOrder`
 * 3. Audience matches user role (`standard` vs `evaluator`)
 * 4. Sorted ascending by `postLoginChunkPreloadOrder` (lower = earlier)
 *
 * @param {Object|null|undefined} userData
 * @returns {string[]} module keys (not routeIds — see `getPostLoginChunkPreloadRouteIds`)
 */
export function getPostLoginChunkPreloadModuleKeys(userData) {
  if (!userData) return [];

  const audience = resolvePostLoginAudience(userData);

  return getModuleCatalogEntries()
    .filter((moduleConfig) => canAccessModule(moduleConfig.key, userData))
    .filter((moduleConfig) => Number.isFinite(moduleConfig.routing?.postLoginChunkPreloadOrder))
    .filter(
      (moduleConfig) =>
        (moduleConfig.routing.postLoginChunkPreloadAudience || "standard") === audience
    )
    .sort(
      (left, right) =>
        left.routing.postLoginChunkPreloadOrder - right.routing.postLoginChunkPreloadOrder
    )
    .map((moduleConfig) => moduleConfig.key);
}
